import { UPDATE_USER_MUTATION } from "@/gql/user";
import { logger } from "@/utils/logger";
import { isValidEmail } from "@/utils/verification";
import { useMutation } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./use-auth";

interface UseEmailSubmissionReturn {
	isEmailAvailable: boolean;
	submitEmail: () => Promise<void>;
	name?: string;
	email?: string;
	setEmail: (_email: string) => void;
	setName: (_name: string) => void;
}

export function useEmailSubmission({ shouldRefetch }: { shouldRefetch?: boolean }): UseEmailSubmissionReturn {
	const { user, refetchUser } = useAuth();
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [isEmailAvailable, setIsEmailAvailable] = useState(false);
	const [updateUserMutation] = useMutation(UPDATE_USER_MUTATION);

	useEffect(() => {
		setIsEmailAvailable(Boolean(user?.emails?.length));
	}, [user?.emails?.length]);

	const submitEmail = useCallback(async () => {
		if (!user) {
			throw new Error("User not authenticated. Please log in and try again.");
		}
		// Validate email parameter
		if (!email.length) {
			throw new Error("Email address is required.");
		}
		if (!isValidEmail(email)) {
			throw new Error("Please enter a valid email address.");
		}

		// Validate name parameter
		if (name && name.trim().length < 2) {
			throw new Error("Name must be at least 2 characters long.");
		}
		if (name && !/^[a-zA-Z\s\-']+$/.test(name.trim())) {
			throw new Error("Name can only contain letters, spaces, hyphens, and apostrophes.");
		}

		try {
			// Prepare the existing emails plus the new one
			const existingEmails = user?.emails || [];

			// Check if email already exists
			const emailExists = existingEmails.some(
				existingEmail => existingEmail.address.toLowerCase() === email.toLowerCase(),
			);

			if (emailExists) {
				logger.info("Duplicate email submission detected", { userId: user?.id });
				return; // Silently succeed since email is already added
			}

			const updatedEmails = [
				...existingEmails,
				{
					address: email.toLowerCase(),
					verified: false,
					type: "personal",
				},
			];

			// Call the GraphQL mutation - Apollo will use the current token from storage
			const { data } = await updateUserMutation({
				variables: {
					params: {
						update: {
							emails: updatedEmails,
							...(name ? { name: name.trim() } : {}),
						},
					},
				},
			});

			if (data?.user?.update) {
				// Update user data with the response from the server
				if (shouldRefetch) await refetchUser(data.user.update);
				logger.info("Email successfully added to user profile", {
					userId: user?.id,
					operation: "email_submission",
					context: "GraphQL mutation for adding user email",
				});
			} else {
				logger.warn("GraphQL mutation completed but returned no user update data", {
					userId: user?.id,
					hasData: !!data,
					hasUser: !!data?.user,
					operation: "email_submission",
				});
				throw new Error("Failed to update user profile. The email may not have been saved.");
			}
		} catch (error) {
			// Log error without PII
			logger.error("Failed to submit email", {
				userId: user?.id,
				errorType: error instanceof Error ? error.constructor.name : typeof error,
				operation: "email_submission",
				context: "GraphQL mutation for adding user email",
			});

			// Always throw a generic error to prevent PII exposure in error messages
			throw new Error("Failed to save email. Please check your information and try again.");
		}
	}, [user, email, name, updateUserMutation, shouldRefetch, refetchUser]);

	return {
		isEmailAvailable,
		submitEmail,
		name,
		email,
		setEmail,
		setName,
	};
}
